import type { UserLegalInfo } from "@/services/profileService";
import { ProfileType } from "@/types";
import {
  type DirtyFieldMap,
  type ProfileFormField,
  type ProfileFormValues,
  COMPANY_BUSINESS_FIELDS,
  CONTACT_FIELDS,
  DOCUMENT_FIELDS,
  PAYMENT_FIELDS,
  PERSON_PERSONAL_FIELDS,
  REGISTERED_ADDRESS_FIELDS,
} from "./profileFormTypes";

function isAnyFieldDirty(
  dirtyFields: DirtyFieldMap,
  fields: readonly ProfileFormField[],
): boolean {
  return fields.some((field) => dirtyFields[field]);
}

/** True when the profile row stores data that change-type would clear. */
export function hasTypeSpecificProfileData(
  profile: UserLegalInfo,
  profileType: ProfileType,
): boolean {
  if (profileType === ProfileType.PERSON) {
    return !!(
      profile.first_name ||
      profile.last_name ||
      profile.birth_date ||
      profile.birth_place
    );
  }
  return !!(
    profile.company_name ||
    profile.vat_number ||
    profile.legal_rep_name
  );
}

export function buildProfileFormDefaults(
  profile: UserLegalInfo,
  fallbackEmail: string,
): ProfileFormValues {
  return {
    first_name: profile.first_name,
    last_name: profile.last_name,
    birth_date: profile.birth_date ?? "",
    birth_place: profile.birth_place,
    fiscal_code: profile.fiscal_code,

    registered_street: profile.registered_street,
    registered_postal_code: profile.registered_postal_code,
    registered_city: profile.registered_city,
    registered_province: profile.registered_province,
    registered_country: profile.registered_country,

    document_type: profile.document_type,
    document_number: profile.document_number,
    document_issuer: profile.document_issuer,
    document_issue_date: profile.document_issue_date ?? "",
    document_expiry_date: profile.document_expiry_date ?? "",

    company_name: profile.company_name,
    vat_number: profile.vat_number,
    pec: profile.pec,
    legal_rep_name: profile.legal_rep_name,

    contact_phone: profile.contact_phone,
    contact_email: profile.contact_email || fallbackEmail,
    partner_notification_channel: profile.partner_notification_channel,
    preferred_language: profile.preferred_language,

    iban: profile.iban,
    account_holder: profile.account_holder,
  };
}

export function isPersonPersonalDirty(dirtyFields: DirtyFieldMap): boolean {
  return isAnyFieldDirty(dirtyFields, PERSON_PERSONAL_FIELDS);
}

export function isRegisteredAddressDirty(dirtyFields: DirtyFieldMap): boolean {
  return isAnyFieldDirty(dirtyFields, REGISTERED_ADDRESS_FIELDS);
}

export function isDocumentDirty(dirtyFields: DirtyFieldMap): boolean {
  return isAnyFieldDirty(dirtyFields, DOCUMENT_FIELDS);
}

export function isCompanyBusinessDirty(dirtyFields: DirtyFieldMap): boolean {
  return isAnyFieldDirty(dirtyFields, COMPANY_BUSINESS_FIELDS);
}

export function isCompanyLegalRepDirty(dirtyFields: DirtyFieldMap): boolean {
  return !!dirtyFields.legal_rep_name || isDocumentDirty(dirtyFields);
}

export function isContactDirty(dirtyFields: DirtyFieldMap): boolean {
  return isAnyFieldDirty(dirtyFields, CONTACT_FIELDS);
}

export function isPaymentDirty(dirtyFields: DirtyFieldMap): boolean {
  return isAnyFieldDirty(dirtyFields, PAYMENT_FIELDS);
}
